import React from 'react';
import moment from 'moment';
import { Box, Grid, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
import { useTheme } from '@mui/material/styles';
import Linkify from 'react-linkify';
import DOMPurify from 'dompurify';
import { FormPaperContainer, GridRow, RowContent, RowTitle } from './FormGrid';
import RescueEditBreadcrumb from './RescueEditBreadcrumb';
import { formatPhoneNumber, formatRescueDistance, formatRescueDuration } from '../../../helpers/formatters';
import {
  getRescueDropoffAddress,
  getRescueFoodTypes,
  getRescuePickupAddress,
  getRescuePickupLocationFullName,
  isHTML,
} from '../../../helpers/RescuesHelper';
import DonorLinkIcon from '../../../components/DonorLinkIcon';
import useHasActiveFeatureFlag from '../../../hooks/useFeatureFlags';
import { FF_DONOR_LINK, FF_USER_GROUPS } from '../../../components/FeatureFlagWrapper';
import DonorLinkRescueDataTable from '../../../components/Common/DonorLinkRescueDataTable';

const handleAddressClick = address => window.open(`https://maps.google.com/?q=${address}`, '_blank');

const addressRowSx = {
  '&:hover': {
    cursor: 'pointer',
  },
};
const breakWhiteSpacesSx = {
  whiteSpace: 'pre-wrap',
};
const donorLinkIconSx = {
  top: 8,
  right: 8,
  position: 'absolute',
};

const RescueDetailsView = ({ rescue, testId, children }) => {
  const foodTypes = getRescueFoodTypes(rescue);
  const pickupAddress = getRescuePickupAddress(rescue);
  const dropoffAddress = getRescueDropoffAddress(rescue);
  const hasDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);
  const hasUserGroupsFF = useHasActiveFeatureFlag(FF_USER_GROUPS);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'));
  const donorLinkReceipt = Array.isArray(rescue?.rescue_confirmed_metadata) ? rescue.rescue_confirmed_metadata : [];
  let rescueDonationGroups = [];

  try {
    rescueDonationGroups = JSON.parse(rescue.donation_groups || '[]')
  } catch (error) {
    console.error('Unable to parse rescue donation groups.');
  }

  return (
    <>
      <RescueEditBreadcrumb isMobile={isMobileView} />
      <Box display="flex" flexWrap="wrap" gap="16px">
        <FormPaperContainer testId={testId} rescueDetails>
          <GridRow>
            <RowTitle>Date:</RowTitle>
            <RowContent>{moment(rescue.date, 'YYYY-MM-DD').format('dddd, MMMM Do')}</RowContent>
          </GridRow>

          <GridRow>
            <RowTitle>From:</RowTitle>
            <RowContent>
              {getRescuePickupLocationFullName(rescue)}
            </RowContent>
          </GridRow>

          <GridRow>
            <RowTitle>To:</RowTitle>
            <RowContent>{rescue.receiver}</RowContent>
          </GridRow>

          <GridRow>
            <RowTitle>
              Distance / Duration:
            </RowTitle>

            <RowContent>
              {`${formatRescueDistance(rescue.distance, ' miles')} / ${formatRescueDuration(rescue.duration, false)}`}
            </RowContent>
          </GridRow>

          <GridRow hidden={!rescue.food_types}>
            <RowTitle>
              Food Type(s):
            </RowTitle>

            <RowContent>
              {foodTypes}
            </RowContent>
          </GridRow>

          <GridRow hidden={!rescue.slug}>
            <RowTitle>
              Description:
            </RowTitle>

            <RowContent>
              {rescue.slug}
            </RowContent>
          </GridRow>

          <GridRow hidden={!rescue.rescue_size}>
            <RowTitle>Rescue Vehicle Size:</RowTitle>
            <RowContent>{rescue.rescue_size}</RowContent>
          </GridRow>

          <GridRow hidden={!hasUserGroupsFF || !rescueDonationGroups.length}>
            <RowTitle>Visible to Groups:</RowTitle>

            <RowContent>{(rescueDonationGroups || []).map(rescueDonationGroup => rescueDonationGroup.name).join(', ')}</RowContent>
          </GridRow>

          <GridRow>{children}</GridRow>

          <GridRow>
            <RowTitle>
              <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                <Grid item>Pickup Address:</Grid>
                <Grid item>
                  <Box ml={4}>
                    <IconButton size="small" aria-label="delete" onClick={() => handleAddressClick(pickupAddress)}>
                      <RoomIcon htmlColor="#000" fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </RowTitle>
            <RowContent>
              <Box component="span" sx={addressRowSx} onClick={() => handleAddressClick(pickupAddress)}>
                {pickupAddress}
              </Box>
            </RowContent>
          </GridRow>

          <GridRow>
            <RowTitle>Pickup Contact:</RowTitle>
            {rescue.pickup_contact && <RowContent>Name: {rescue.pickup_contact}</RowContent>}
            {rescue.pickup_contact_phone && (
              <RowContent>
                <span>Phone: </span>
                <a href={`tel:${rescue.pickup_contact_phone}`}>
                  {formatPhoneNumber(rescue.pickup_contact_phone)}
                </a>
                {rescue.pickup_contact_phone_ext && <span>{` (ext: ${rescue.pickup_contact_phone_ext})`}</span>}
              </RowContent>
            )}
            {rescue.pickup_contact_email && (
              <RowContent>
                Email: <a href={`mailto:${rescue.pickup_contact_email}`}>{rescue.pickup_contact_email}</a>
              </RowContent>
            )}
          </GridRow>

          <GridRow hidden={!rescue.pickup_notes}>
            <RowTitle>Pickup Notes:</RowTitle>
            <RowContent>
              {isHTML(rescue?.pickup_notes) ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rescue?.pickup_notes) }} />
              ) : (
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a target="blank" href={decoratedHref} key={key}>
                      {decoratedText}
                    </a>
                )}
                >
                  <Box component="span" sx={{ breakWhiteSpacesSx }}>
                    {rescue?.pickup_notes}
                  </Box>
                </Linkify>
              )}
            </RowContent>
          </GridRow>

          <GridRow>
            <RowTitle>
              <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                <Grid item>Dropoff Address:</Grid>
                <Grid item>
                  <Box ml={4}>
                    <IconButton size="small" aria-label="delete" onClick={() => handleAddressClick(dropoffAddress)}>
                      <RoomIcon htmlColor="#000" fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </RowTitle>
            <RowContent>
              <Box component="span" sx={addressRowSx} onClick={() => handleAddressClick(dropoffAddress)}>
                {dropoffAddress}
              </Box>
            </RowContent>
          </GridRow>

          <GridRow>
            <RowTitle>Dropoff Contact:</RowTitle>
            {rescue.dropoff_contact && <RowContent>Name: {rescue.dropoff_contact}</RowContent>}
            {rescue.dropoff_contact_phone && (
              <RowContent>
                <span>Phone: </span>
                <a href={`tel:${rescue.dropoff_contact_phone}`}>
                  {formatPhoneNumber(rescue.dropoff_contact_phone)}
                </a>
                {rescue.dropoff_contact_phone_ext && <span>{` (ext: ${rescue.dropoff_contact_phone_ext})`}</span>}
              </RowContent>
            )}
            {rescue.dropoff_contact_email && (
              <RowContent>
                Email: <a href={`mailto:${rescue.dropoff_contact_email}`}>{rescue.dropoff_contact_email}</a>
              </RowContent>
            )}
          </GridRow>

          <GridRow hidden={!rescue.dropoff_notes}>
            <RowTitle>Dropoff Notes:</RowTitle>
            <RowContent>
              {isHTML(rescue?.dropoff_notes) ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rescue?.dropoff_notes) }} />
              ) : (
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a target="blank" href={decoratedHref} key={key}>
                      {decoratedText}
                    </a>
                )}
                >
                  <Box component="span" sx={breakWhiteSpacesSx}>{rescue?.dropoff_notes}</Box>
                </Linkify>
              )}
            </RowContent>
          </GridRow>

          {(hasDonorLinkFF && !!rescue.location_donor_link_active) && (
            <Tooltip arrow placement="top" title="This is a Donor Link rescue">
              <Box component="span" sx={donorLinkIconSx}>
                <DonorLinkIcon size={24} isActive />
              </Box>
            </Tooltip>
          )}
        </FormPaperContainer>
        {!!rescue.rescue_confirmed && !!donorLinkReceipt.length && (
          <FormPaperContainer rescueDetails>
            <Typography variant="h6">Donor Receipt</Typography>
            <DonorLinkRescueDataTable rescueData={donorLinkReceipt} />
          </FormPaperContainer>
        )}
      </Box>
    </>
  );
};

export default RescueDetailsView;
